/**
 * Application-wide constants for the Tappanon frontend.
 */

// API Configuration
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
export const API_TIMEOUT_MS = 30000; // 30 seconds

// Token Configuration
export const ACCESS_TOKEN_KEY = 'access_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry

// Retry Configuration
export const MAX_RETRY_ATTEMPTS = 3;
export const RETRY_DELAY_MS = 1000;
export const RETRY_BACKOFF_MULTIPLIER = 2;

// Pagination
export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;

// Document Settings
export const DOCUMENT_TITLE_MAX_LENGTH = 255;
export const DOCUMENT_MAX_FILE_SIZE_MB = 10;
export const ALLOWED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown'
];

// Comment Settings
export const COMMENT_MAX_LENGTH = 5000;
export const COMMENT_MIN_LENGTH = 1;

// Profile Settings
export const BIO_MAX_LENGTH = 2000;
export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 30;
export const COMPANY_NAME_MAX_LENGTH = 255;

// Image Upload
export const PROFILE_PICTURE_MAX_SIZE_MB = 5;
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// Verification
export const VERIFICATION_CODE_LENGTH = 6;
export const VERIFICATION_RESEND_COOLDOWN_SECONDS = 60;

// UI Debounce/Throttle
export const SEARCH_DEBOUNCE_MS = 300;
export const SCROLL_THROTTLE_MS = 200;
export const AUTO_SAVE_DEBOUNCE_MS = 2000;

// Toast/Notification Duration
export const TOAST_SUCCESS_DURATION_MS = 3000;
export const TOAST_ERROR_DURATION_MS = 5000;
export const TOAST_INFO_DURATION_MS = 4000;

// Query Cache Times (React Query)
export const CACHE_TIME_MS = 5 * 60 * 1000; // 5 minutes
export const STALE_TIME_MS = 1 * 60 * 1000; // 1 minute

// Validation Patterns
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const USERNAME_REGEX = /^[a-zA-Z0-9_-]+$/;

// Route Paths
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  VERIFY_EMAIL: '/verify-email',
  ONBOARDING: '/onboarding',
  EMPLOYMENT_VERIFICATION_SUBMIT: '/employment-verification/submit',
  EMPLOYMENT_VERIFICATION_CONFIRM: '/employment-verification/confirm',
  PROFILE: '/profile',
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'Session expired. Please log in again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'Server error. Please try again later.',
  RATE_LIMIT: 'Too many requests. Please wait before trying again.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  UNKNOWN_ERROR: 'An unknown error occurred. Please try again.',
} as const;

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  TOO_MANY_REQUESTS: 429,
  SERVER_ERROR: 500,
} as const;
